/**
 * Content Sheet — Feb 2026
 *
 * Spreadsheet-style editorial view of content_items. Writers filter by
 * brand / project / status / month → hit Apply → see rows with inline-
 * editable Deliverable / Topic / Caption / Pillar / Internal notes /
 * Channels / Attachments. Edits debounce (5s) + flush on blur, so no
 * manual Save button is needed.
 *
 * Why no auto-load: the Portalwiz workspace has 10k+ content items.
 * Default fetching would stall the page and burn API budget. The
 * filters-first flow mirrors the shape of a saved query so writers
 * can bookmark the Apply URL (via `?filters=…` → phase 2).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Filter, Search, Loader2, Image as ImageIcon, Link2, Paperclip, Save, CheckCircle2, X, Plus, ExternalLink, Download, Share2, Pencil, Eye, Copy, Trash2, Bell, MessageSquare, Check as CheckIcon, ChevronsUpDown } from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { clientsLookup, brandsLookup, projectsLookup } from "@/lib/lookupCache";
// Nov 2026 · Modal editor replaces the "open in new window" link so the
// writer never loses their place on the sheet. Lazy-import so Content
// Sheet's first paint doesn't pull SmartPostEditor's deps.
import SmartPostEditor from "@/pages/app/SmartPostEditor";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import SmartRichText from "@/components/SmartRichText";
import EmptyState from "@/components/EmptyState";

const CHANNELS = [
  { value: "instagram", label: "IG" },
  { value: "linkedin",  label: "LI" },
  { value: "facebook",  label: "FB" },
  { value: "twitter",   label: "X" },
  { value: "youtube",   label: "YT" },
  { value: "tiktok",    label: "TT" },
  { value: "pinterest", label: "PT" },
  { value: "whatsapp",  label: "WA" },
  { value: "email",     label: "EM" },
  { value: "blog",      label: "BL" },
];

const STATUS_OPTIONS = ["planned", "in_progress", "review", "approved", "published", "rejected"];

const _thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Feb 2026 · Lightweight HTML → text strip for the Excel caption dump.
// Not a security boundary — the Caption field is already validated by
// the SmartRichText editor. Just removes tags and decodes a few basic
// entities so the exported cells read naturally in Excel.
function _stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/\s+/g, " ")
    .trim();
}

/* ──────── Sorted, searchable, multi-select combobox ────────────── */

function MultiCombobox({ label, options, values, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => (o.name || "").toLowerCase().includes(s)) : options;
  }, [options, q]);
  const toggle = (id) => {
    if (values.includes(id)) onChange(values.filter((v) => v !== id));
    else onChange([...values, id]);
  };
  const buttonLabel = values.length === 0
    ? label
    : values.length === 1
    ? (options.find((o) => o.id === values[0])?.name || label)
    : `${values.length} ${label.toLowerCase()}s`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline"
                className={`h-9 justify-between text-xs rounded-none px-2 ${values.length === 0 ? "text-muted-foreground font-normal" : "text-foreground"}`}
                data-testid={testid}>
          <span className="truncate">{buttonLabel}</span>
          <ChevronsUpDown className="w-3 h-3 opacity-50 ml-1 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0 rounded-none" align="start" data-testid={`${testid}-panel`}>
        <div className="p-2 border-b border-border">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder={`Search ${label.toLowerCase()}…`}
                 className="h-8 rounded-none text-xs"
                 data-testid={`${testid}-search`}
                 autoFocus />
        </div>
        <div className="flex items-center justify-between px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold border-b border-border">
          <span>{filtered.length} {label.toLowerCase()}s</span>
          <div className="flex items-center gap-2">
            {values.length > 0 && (
              <button type="button" onClick={() => onChange([])}
                      className="normal-case tracking-normal text-primary hover:underline"
                      data-testid={`${testid}-clear`}>
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="max-h-[260px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground italic">No matches</div>
          ) : filtered.map((o) => {
            const checked = values.includes(o.id);
            return (
              <label key={o.id}
                     className={`flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-muted/40 ${checked ? "bg-primary/5" : ""}`}
                     data-testid={`${testid}-opt-${o.id}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(o.id)} className="h-3.5 w-3.5" />
                <span className="truncate">{o.name}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}


export default function ContentSheet() {
  const { activeWorkspaceId } = useAuth();
  const { hasPerm } = useWorkspacePerms();
  const canEdit = hasPerm("content_sheet.edit") || hasPerm("content_calendar.manage")
                   || hasPerm("workspace.admin") || hasPerm("workspace.manage");

  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  // Draft filter state (not applied until Apply). Client / Brand /
  // Project are now multi-select arrays so writers can preview
  // multiple accounts at once.
  const [draft, setDraft] = useState({
    month: _thisMonth(),
    client_ids: [], brand_ids: [], project_ids: [],
    status: "", category: "", q: "",
  });
  // Applied filter state. `null` ⇒ not applied yet (empty state).
  const [applied, setApplied] = useState(null);
  const [rows, setRows] = useState([]);
  // Nov 2026 · In-memory quick search over loaded rows (no re-fetch).
  // Declared here (above the memos that use it) to avoid a TDZ error in
  // production builds where const/let hoisting throws before init.
  const [quickSearch, setQuickSearch] = useState("");
  // Nov 2026 · Group-by dropdown. "none" = flat; otherwise group rows
  // under section headers keyed on this field.
  const [groupBy, setGroupBy] = useState("none");
  // Nov 2026 · Client-side filter + group memos. QuickSearch narrows the
  // loaded `rows` by stringified fields; groupBy rolls them into
  // section buckets consumed by SheetTable.
  const filteredRows = useMemo(() => {
    const q = quickSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const bag = [r.name, r.topic, r.pillar, r.notes, r.brand_name,
                     r.project_name, r.client_name, r.caption,
                     r.onscreen_content, r.status, r.category,
                     ...(r.channels || []), ...(r.hashtags || [])]
                     .filter(Boolean).join(" ").toLowerCase();
      return bag.includes(q);
    });
  }, [rows, quickSearch]);
  const groupedRows = useMemo(() => {
    if (groupBy === "none") return [{ key: "", label: "", items: filteredRows }];
    const keyFor = (r) => {
      switch (groupBy) {
        case "client": return r.client_name || "— no client —";
        case "brand": return r.brand_name || "— no brand —";
        case "project": return r.project_name || "— no project —";
        case "category": return r.category || "— no category —";
        case "channel": return (r.channels && r.channels[0]) || "— no channel —";
        case "status": return r.status || "planned";
        default: return "";
      }
    };
    const buckets = new Map();
    for (const r of filteredRows) {
      const k = keyFor(r);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(r);
    }
    return Array.from(buckets.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, items]) => ({ key, label: key, items }));
  }, [filteredRows, groupBy]);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState({ scoped: false, elevated: false });

  useEffect(() => {
    if (!activeWorkspaceId) return;
    // Nov 2026 · Use cached lookup endpoints (id/name only) instead of
    // the heavy list endpoints so the filter bar renders instantly even
    // on workspaces with 500+ brands/projects.
    Promise.all([
      brandsLookup(activeWorkspaceId),
      projectsLookup(activeWorkspaceId),
      clientsLookup(activeWorkspaceId),
    ]).then(([b, p, c]) => {
      setBrands(b || []);
      setProjects(p || []);
      setClients(c || []);
    });
  }, [activeWorkspaceId]);

  const applyFilters = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (draft.month) params.set("month", draft.month);
      // Multi-select filters go out as comma-separated strings — the
      // backend splits them back into `$in` queries.
      if (draft.client_ids.length) params.set("client_id", draft.client_ids.join(","));
      if (draft.brand_ids.length)   params.set("brand_id",   draft.brand_ids.join(","));
      if (draft.project_ids.length) params.set("project_id", draft.project_ids.join(","));
      if (draft.status)   params.set("status",   draft.status);
      if (draft.category) params.set("category", draft.category);
      if (draft.q)        params.set("q",        draft.q);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/content-sheet?${params.toString()}`);
      setRows(data.rows || []);
      setScope({ scoped: data.scoped_to_projects, elevated: data.elevated });
      setApplied({ ...draft });
      if ((data.rows || []).length === 0) {
        toast.info(data.scoped_to_projects
          ? "No items in your project memberships for this filter"
          : "No items match this filter");
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load content sheet"));
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setDraft({ month: _thisMonth(), client_ids: [], brand_ids: [], project_ids: [], status: "", category: "", q: "" });
  };

  // In-place row update after a successful save.
  const patchRowLocal = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  // Feb 2026 · "Writer's view" toggle — hides Project/Brand/Channels,
  // reveals Hashtags, and expands the Caption cell into a full-width
  // inline SmartRichText instead of a popover. Perfect for the actual
  // content-writing pass; the default "Full" view is for PMs scanning
  // channel coverage.
  const [writersView, setWritersView] = useState(false);

  // Nov 2026 · Deliverable editor modal state. The row-level icon used
  // to be an "open in new window" <a> that lost the PM's filter + scroll
  // context. We now mount SmartPostEditor as a modal above the sheet
  // and refresh the row in place when it closes.
  const [editorItem, setEditorItem] = useState(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const openDeliverableEditor = async (rowId) => {
    setEditorLoading(true);
    try {
      const { data } = await api.get(`/content-items/${rowId}`);
      setEditorItem(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not load deliverable"));
    } finally {
      setEditorLoading(false);
    }
  };

  // Nov 2026 · Freeze N columns. Persist the choice in localStorage so
  // writers / PMs don't have to re-pick on every session. The CSS
  // sticky approach needs cumulative `left` offsets per column, so we
  // keep both the current count and the ordered column widths in
  // SheetTable; the picker here just stores the integer.
  const [frozenCols, setFrozenCols] = useState(() => {
    try { return Number(localStorage.getItem("cs-frozen-cols")) || 0; }
    catch { return 0; }
  });
  const changeFrozen = (n) => {
    setFrozenCols(n);
    try { localStorage.setItem("cs-frozen-cols", String(n)); } catch { /* ignore */ }
  };

  // Nov 2026 · Wrap/Unwrap/Crop toggle (like Google Sheets):
  //   wrap   — cells grow vertically to fit full content
  //   nowrap — single line + ellipsis (default)
  //   crop   — single line truncated; all rows forced to one line height
  const [wrapMode, setWrapMode] = useState(() => {
    try { return localStorage.getItem("cs-wrap-mode") || "nowrap"; }
    catch { return "nowrap"; }
  });
  const changeWrapMode = (m) => {
    setWrapMode(m);
    try { localStorage.setItem("cs-wrap-mode", m); } catch { /* ignore */ }
  };

  // Feb 2026 · Excel export — client-side via xlsx. Dumps the currently
  // visible (filtered) rows with every CC-relevant column so clients
  // and stakeholders who want offline review get a tidy workbook.
  const exportExcel = () => {
    if (rows.length === 0) {
      toast.error("No rows to export");
      return;
    }
    const brandNameById = Object.fromEntries(brands.map((b) => [b.id, b.name]));
    const projectNameById = Object.fromEntries(projects.map((p) => [p.id, p.name]));
    const data = rows.map((r) => ({
      "Planned date":   r.planned_date || "",
      "Deliverable":    r.name || "",
      "Topic":          r.topic || "",
      "Project":        projectNameById[r.project_id] || r.project_name || "",
      "Brand":          brandNameById[r.brand_id] || r.brand_name || "",
      "Category":       r.category || "",
      "Content pillar": r.content_pillar || r.pillar || "",
      "Onscreen content": _stripHtml(r.onscreen_content || ""),
      "Status":         r.status || "",
      "Channels":       (r.channels || []).join(", "),
      "Caption":        _stripHtml(r.caption || ""),
      "Hashtags":       Array.isArray(r.hashtags) ? r.hashtags.join(" ") : (r.hashtags || ""),
      "Internal notes": r.internal_notes || "",
      "Attachments":    (r.attachments || []).length,
      "External links": (r.external_links || []).map((l) => l.url).join(" | "),
      "Updated at":     r.updated_at || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    // Width hints — makes the raw export readable without manual
    // column-fit in Excel/Numbers/Sheets.
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({
      wch: Math.min(60, Math.max(k.length + 2, ...data.map((d) => String(d[k] || "").length + 2))),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Content Sheet");
    const monthTag = applied?.month || _thisMonth();
    XLSX.writeFile(wb, `content-sheet-${monthTag}.xlsx`);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"} to Excel`);
  };

  // Feb 2026 · Share dialog state. Opening it lazy-loads existing
  // shares for this workspace so the "revoke" + unread-count UI is
  // instantly interactive.
  const [shareOpen, setShareOpen] = useState(false);

  // Feb 2026 · Client feedback — load ALL feedback (read + unread) for
  // the current workspace so every row with any history keeps its
  // comment icon visible. The writer can click it to re-read the
  // thread, reply inline, or mark unread items as read.
  const [feedback, setFeedback] = useState([]);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxLoading, setInboxLoading] = useState(false);
  const loadFeedback = async () => {
    if (!activeWorkspaceId) return;
    setInboxLoading(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/content-sheet/feedback`,
      );
      setFeedback(Array.isArray(data) ? data : []);
    } catch (e) {
      // Soft-fail — perm issue or endpoint missing shouldn't break the sheet.
    } finally { setInboxLoading(false); }
  };
  useEffect(() => { loadFeedback(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);
  // Refresh after Apply so a newly filtered sheet always has fresh counts.
  useEffect(() => { if (applied) loadFeedback(); /* eslint-disable-next-line */ }, [applied]);

  // Group feedback by content_item_id → always-sorted oldest-first.
  const feedbackByItem = useMemo(() => {
    const m = {};
    for (const f of feedback) {
      if (!f?.content_item_id) continue;
      (m[f.content_item_id] = m[f.content_item_id] || []).push(f);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    }
    return m;
  }, [feedback]);

  // Bell dropdown still focuses on the unread subset — unread only here.
  const unread = useMemo(() => feedback.filter((f) => !f.read_at), [feedback]);

  // Local mutators (optimistic) so a mark-read/reply doesn't require
  // a full feedback refetch.
  const applyFeedbackPatch = (fid, patch) => {
    setFeedback((arr) => arr.map((f) => (f.id === fid ? { ...f, ...patch } : f)));
  };
  const appendReply = (fid, reply) => {
    setFeedback((arr) => arr.map((f) =>
      f.id === fid ? { ...f, read_at: f.read_at || new Date().toISOString(), replies: [...(f.replies || []), reply] } : f));
  };

  const markOneRead = async (fid) => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/content-sheet/feedback/${fid}/read`);
      applyFeedbackPatch(fid, { read_at: new Date().toISOString() });
    } catch (e) { toast.error(apiErrorMessage(e, "Could not mark as read")); }
  };
  const markAllRead = async () => {
    const ids = unread.map((f) => f.id);
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((fid) =>
        api.post(`/workspaces/${activeWorkspaceId}/content-sheet/feedback/${fid}/read`)));
      const nowIso = new Date().toISOString();
      setFeedback((arr) => arr.map((f) => (ids.includes(f.id) ? { ...f, read_at: nowIso } : f)));
      toast.success(`Marked ${ids.length} as read`);
    } catch (e) { toast.error(apiErrorMessage(e, "Mark all failed")); }
  };
  const sendReply = async (fid, text) => {
    if (!text?.trim()) return;
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/content-sheet/feedback/${fid}/reply`,
        { text: text.trim() },
      );
      appendReply(fid, data);
      toast.success("Reply sent");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not send reply")); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="content-sheet-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Content Sheet</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Spreadsheet view of deliverables · inline edits auto-save
            {scope.scoped && !scope.elevated && " · scoped to your projects"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="cs-toolbar">
          {rows.length > 0 && (
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground bg-muted border border-border px-2 py-1">
              {rows.length} item{rows.length === 1 ? "" : "s"}
            </span>
          )}
          {/* Feb 2026 · View-mode toggle (PM full vs Writer's focus). */}
          <div className="inline-flex border border-border" role="tablist">
            <button type="button"
                    onClick={() => setWritersView(false)}
                    className={`h-8 px-2 text-[10px] uppercase tracking-widest font-semibold ${!writersView ? "bg-foreground text-background" : "bg-background text-muted-foreground hover:bg-muted"}`}
                    aria-pressed={!writersView}
                    data-testid="cs-view-full">
              <Eye className="w-3 h-3 inline mr-1" /> Full
            </button>
            <button type="button"
                    onClick={() => setWritersView(true)}
                    className={`h-8 px-2 text-[10px] uppercase tracking-widest font-semibold border-l border-border ${writersView ? "bg-foreground text-background" : "bg-background text-muted-foreground hover:bg-muted"}`}
                    aria-pressed={writersView}
                    data-testid="cs-view-writer">
              <Pencil className="w-3 h-3 inline mr-1" /> Writer
            </button>
          </div>
          {/* Nov 2026 · Freeze N columns picker */}
          <Select value={String(frozenCols)} onValueChange={(v) => changeFrozen(Number(v))}>
            <SelectTrigger className="h-8 w-[110px] text-[10px] uppercase tracking-widest rounded-none" data-testid="cs-freeze-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">No freeze</SelectItem>
              <SelectItem value="1">Freeze 1</SelectItem>
              <SelectItem value="2">Freeze 2</SelectItem>
              <SelectItem value="3">Freeze 3</SelectItem>
              <SelectItem value="4">Freeze 4</SelectItem>
            </SelectContent>
          </Select>
          {/* Nov 2026 · Group-by picker */}
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-8 w-[130px] text-[10px] uppercase tracking-widest rounded-none" data-testid="cs-groupby-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No group</SelectItem>
              <SelectItem value="client">Group · Client</SelectItem>
              <SelectItem value="brand">Group · Brand</SelectItem>
              <SelectItem value="project">Group · Project</SelectItem>
              <SelectItem value="category">Group · Category</SelectItem>
              <SelectItem value="channel">Group · Channel</SelectItem>
              <SelectItem value="status">Group · Status</SelectItem>
            </SelectContent>
          </Select>
          {/* Nov 2026 · Wrap/Unwrap/Crop toggle */}
          <div className="inline-flex border border-border overflow-hidden" data-testid="cs-wrap-toggle">
            <button type="button"
                     onClick={() => changeWrapMode("wrap")}
                     className={`px-2 py-1 text-[10px] uppercase tracking-widest ${wrapMode === "wrap" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                     data-testid="cs-wrap-wrap">Wrap</button>
            <button type="button"
                     onClick={() => changeWrapMode("nowrap")}
                     className={`px-2 py-1 text-[10px] uppercase tracking-widest border-l border-border ${wrapMode === "nowrap" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                     data-testid="cs-wrap-nowrap">Normal</button>
            <button type="button"
                     onClick={() => changeWrapMode("crop")}
                     className={`px-2 py-1 text-[10px] uppercase tracking-widest border-l border-border ${wrapMode === "crop" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                     data-testid="cs-wrap-crop">Crop</button>
          </div>
          <Button size="sm" variant="outline" onClick={exportExcel}
                   disabled={rows.length === 0}
                   className="h-8 text-xs rounded-none" data-testid="cs-excel-export">
            <Download className="w-3 h-3 mr-1" /> Excel
          </Button>
          {/* Feb 2026 · Unread Feedback Inbox bell */}
          <Popover open={inboxOpen} onOpenChange={setInboxOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 text-xs rounded-none relative"
                      data-testid="cs-inbox-toggle"
                      title="Client feedback inbox">
                <Bell className="w-3 h-3" />
                {unread.length > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center rounded-full"
                        data-testid="cs-inbox-badge">
                    {unread.length > 99 ? "99+" : unread.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[380px] p-0 rounded-none" align="end" data-testid="cs-inbox-panel">
              <div className="flex items-center justify-between p-2 border-b border-border bg-muted/40">
                <div className="text-[10px] uppercase tracking-widest font-semibold">
                  Client feedback {unread.length > 0 && <span className="text-rose-700">· {unread.length} new</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={loadFeedback}
                          className="text-[10px] text-muted-foreground hover:text-primary"
                          data-testid="cs-inbox-refresh"
                          title="Refresh">
                    {inboxLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
                  </button>
                  {unread.length > 0 && (
                    <button type="button" onClick={markAllRead}
                            className="text-[10px] text-primary hover:underline"
                            data-testid="cs-inbox-markall">
                      Mark all read
                    </button>
                  )}
                </div>
              </div>
              <div className="max-h-[380px] overflow-y-auto">
                {unread.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground italic" data-testid="cs-inbox-empty">
                    All caught up — no unread feedback.
                  </div>
                ) : unread.map((f) => (
                  <InboxItem key={f.id} fb={f}
                             onMarkRead={() => markOneRead(f.id)} />
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {hasPerm("content_sheet.share") || hasPerm("workspace.admin") || hasPerm("workspace.manage") ? (
            <Button size="sm" variant="outline" onClick={() => setShareOpen(true)}
                     className="h-8 text-xs rounded-none" data-testid="cs-share-open">
              <Share2 className="w-3 h-3 mr-1" /> Share
            </Button>
          ) : null}
        </div>
      </div>

      {/* FILTERS BAR */}
      <div className="border border-border bg-muted/30 p-3 space-y-2" data-testid="content-sheet-filters">
        <div className="flex items-center gap-2 text-xs">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="uppercase tracking-widest text-muted-foreground font-semibold">Filters</span>
          <span className="text-muted-foreground">— data loads on Apply only</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <Input type="month" value={draft.month} onChange={(e) => setDraft({ ...draft, month: e.target.value })}
                 className="h-9 text-xs" data-testid="cs-f-month" placeholder="Month" />
          <MultiCombobox label="Client"
                         options={[...clients].sort((a, b) => (a.name || "").localeCompare(b.name || ""))}
                         values={draft.client_ids}
                         onChange={(vals) => setDraft({ ...draft, client_ids: vals })}
                         testid="cs-f-client" />
          <MultiCombobox label="Brand"
                         options={[...brands].sort((a, b) => (a.name || "").localeCompare(b.name || ""))}
                         values={draft.brand_ids}
                         onChange={(vals) => setDraft({ ...draft, brand_ids: vals })}
                         testid="cs-f-brand" />
          <MultiCombobox label="Project"
                         options={[...projects].sort((a, b) => (a.name || "").localeCompare(b.name || ""))}
                         values={draft.project_ids}
                         onChange={(vals) => setDraft({ ...draft, project_ids: vals })}
                         testid="cs-f-project" />
          <Select value={draft.status || "all"} onValueChange={(v) => setDraft({ ...draft, status: v === "all" ? "" : v })}>
            <SelectTrigger className="h-9 text-xs rounded-none" data-testid="cs-f-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="text" value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })}
                 placeholder="Search title / topic / caption"
                 className="h-9 text-xs md:col-span-2 lg:col-span-1"
                 data-testid="cs-f-q"
                 onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }} />
          <div className="flex items-center gap-1 lg:justify-end">
            <Button size="sm" onClick={applyFilters} disabled={loading}
                     className="h-9 text-xs rounded-none" data-testid="cs-apply">
              {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
              Apply
            </Button>
            <Button size="sm" variant="outline" onClick={resetFilters}
                     className="h-9 text-xs rounded-none" data-testid="cs-reset">
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* RESULTS */}
      {applied === null ? (
        <EmptyState
          icon={Filter}
          title="Pick your filters and tap Apply"
          description="Nothing is loaded by default — choose a month / brand / project and tap Apply to pull your rows. Keeps the page snappy on large workspaces."
          testid="content-sheet-empty"
        />
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Filter}
          title={scope.scoped && !scope.elevated ? "Nothing in your project memberships" : "No items match"}
          description="Loosen the filters and tap Apply again."
          testid="content-sheet-empty-filtered"
        />
      ) : (
      <>
      {/* Nov 2026 · Quick client-side search bar above the table.
          Narrows the already-loaded rows against any stringified
          field so PMs don't need to re-fire Apply for a text filter. */}
      {rows.length > 0 && (
        <div className="flex items-center gap-2 pb-1" data-testid="cs-quick-search-wrap">
          <Input
            value={quickSearch}
            onChange={(e) => setQuickSearch(e.target.value)}
            placeholder={`Quick search across ${rows.length} loaded rows…`}
            className="h-8 text-xs rounded-none max-w-md"
            data-testid="cs-quick-search"
          />
          {quickSearch && (
            <Button size="sm" variant="ghost" className="h-8 text-xs"
                     onClick={() => setQuickSearch("")} data-testid="cs-quick-search-clear">Clear</Button>
          )}
          <div className="text-[11px] text-muted-foreground">
            {filteredRows.length} / {rows.length} rows
          </div>
        </div>
      )}
        <SheetTable rows={filteredRows} groups={groupedRows} canEdit={canEdit} onRowChange={patchRowLocal} brands={brands} projects={projects} writersView={writersView}
                    feedbackByItem={feedbackByItem}
                    onMarkRead={markOneRead}
                    onReply={sendReply}
                    wsId={activeWorkspaceId}
                    onFeedbackAdded={(cid, fb) => setFeedback((arr) => [...arr, fb])}
                    onOpenEditor={openDeliverableEditor}
                    frozenCols={frozenCols}
                    wrapMode={wrapMode}
                    groupBy={groupBy} />
      </>
      )}

      {/* Nov 2026 · Deliverable modal editor — reuses the SmartPostEditor
          used on Content Calendar so every field (not just caption) is
          reachable without leaving the sheet. Refreshes the row in
          place on save. */}
      {editorLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 pointer-events-none">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      )}
      {editorItem && (
        <SmartPostEditor
          item={editorItem}
          onClose={() => setEditorItem(null)}
          onChanged={async () => {
            try {
              const { data } = await api.get(`/content-items/${editorItem.id}`);
              patchRowLocal(editorItem.id, data || {});
            } catch { /* non-fatal */ }
            setEditorItem(null);
          }}
        />
      )}

      {shareOpen && (
        <ShareDialog
          wsId={activeWorkspaceId}
          currentFilters={applied || draft}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

/* ──────── Sheet table ───────────────────────────────────────────── */

// Feb 2026 · Column keys drive the resizable widths. Different sets for
// writer vs full view; widths persist per-view in localStorage so a PM
// resizing Caption to 500px keeps that layout next session.
// Feb 2026 · Onscreen Content column sits between Pillar and Caption.
// It carries the text that appears IN the creative (headline / overlay),
// distinct from `caption` which is the post body copy.
const COL_KEYS_FULL = ["date","deliverable","topic","project","brand","pillar","onscreen","caption","notes","channels","status","attach"];
const COL_KEYS_WRITER = ["date","deliverable","topic","pillar","onscreen","caption","hashtags","notes","status","attach"];
const DEFAULT_WIDTHS_FULL = { date:110, deliverable:200, topic:200, project:110, brand:120, pillar:140, onscreen:260, caption:320, notes:200, channels:160, status:120, attach:200 };
const DEFAULT_WIDTHS_WRITER = { date:110, deliverable:200, topic:200, pillar:140, onscreen:320, caption:520, hashtags:200, notes:200, status:120, attach:240 };
const MIN_COL_WIDTH = 60;

function loadStoredWidths(view) {
  try {
    const raw = localStorage.getItem(`cs-col-widths-${view}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch { return null; }
}

function SheetTable({ rows, groups, canEdit, onRowChange, brands, projects, writersView = false, feedbackByItem = {}, onMarkRead, onReply, wsId, onFeedbackAdded, onOpenEditor, frozenCols = 0, wrapMode = "nowrap", groupBy = "none" }) {
  const viewKey = writersView ? "writer" : "full";
  const keys = writersView ? COL_KEYS_WRITER : COL_KEYS_FULL;
  const defaults = writersView ? DEFAULT_WIDTHS_WRITER : DEFAULT_WIDTHS_FULL;
  // Nov 2026 · Writer / PM can drag the right edge of any column header
  // to resize. Widths are keyed (not indexed) so adding a column later
  // doesn't scramble a user's saved layout.
  const [colW, setColW] = useState(() => {
    const stored = loadStoredWidths(viewKey) || {};
    return { ...defaults, ...stored };
  });
  // Reload when view mode flips (writer <-> full) — each view has its
  // own persisted widths map.
  useEffect(() => {
    const stored = loadStoredWidths(viewKey) || {};
    setColW({ ...defaults, ...stored });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey]);
  const widthOf = (k) => colW[k] ?? defaults[k] ?? 120;
  const resizeCol = (k, w) => {
    setColW((prev) => {
      const next = { ...prev, [k]: Math.max(MIN_COL_WIDTH, Math.round(w)) };
      try { localStorage.setItem(`cs-col-widths-${viewKey}`, JSON.stringify(next)); } catch {/* ignore */}
      return next;
    });
  };
  const colWidths = keys.map(widthOf);
  const totalWidth = colWidths.reduce((a, b) => a + b, 0);
  // Index map so SheetRow can look up a column's left-offset in O(1).
  const stickyOffsets = colWidths.reduce((acc, w, i) => {
    acc.push((acc[i - 1] || 0) + (i === 0 ? 0 : colWidths[i - 1]));
    return acc;
  }, []);
  // How many of our THEAD cells are "frozen". We clamp to the actual
  // column count so a user who picked "Freeze 4" on Writer view (which
  // has fewer columns) still gets a sensible layout.
  const effFrozen = Math.min(frozenCols, colWidths.length);
  const stickyClass = (i) =>
    i < effFrozen
      ? "sticky z-20 bg-background shadow-[inset_-1px_0_0_0_hsl(var(--border))]"
      : "";
  const stickyStyle = (i) =>
    i < effFrozen ? { left: stickyOffsets[i] } : undefined;
  return (
    <>
      {/* Feb 2026 · Mobile card-stack. Writers often draft captions
          on their phone during a shoot — the full spreadsheet is
          brutal below 700px. Stacked cards keep every field within
          thumb reach and let the SmartRichText caption breathe. */}
      <div className="md:hidden space-y-2" data-testid="content-sheet-cards-wrap">
        {rows.map((r) => (
          <SheetCard key={r.id} row={r} canEdit={canEdit} onChange={onRowChange}
                     brands={brands} projects={projects} writersView={writersView}
                     feedback={feedbackByItem[r.id] || []}
                     onMarkRead={onMarkRead}
                     onReply={onReply}
                     wsId={wsId}
                     onFeedbackAdded={onFeedbackAdded}
                     onOpenEditor={onOpenEditor} />
        ))}
        {rows.length === 0 && (
          <div className="border border-border p-6 text-center text-xs text-muted-foreground italic">
            No deliverables match these filters.
          </div>
        )}
      </div>
      <div className="hidden md:block border border-border overflow-x-auto" data-testid="content-sheet-table-wrap">
      <table className="w-full text-xs border-collapse" style={{ minWidth: `${totalWidth}px`, tableLayout: "fixed" }}>
        <thead className="bg-muted/60 sticky top-0 z-10">
          <tr>
            <Th w={widthOf("date")} colKey="date" onResize={resizeCol} stickyClass={stickyClass(0)} stickyStyle={stickyStyle(0)}>Date</Th>
            <Th w={widthOf("deliverable")} colKey="deliverable" onResize={resizeCol} stickyClass={stickyClass(1)} stickyStyle={stickyStyle(1)}>Deliverable</Th>
            <Th w={widthOf("topic")} colKey="topic" onResize={resizeCol} stickyClass={stickyClass(2)} stickyStyle={stickyStyle(2)}>Topic</Th>
            {!writersView && <Th w={widthOf("project")} colKey="project" onResize={resizeCol} stickyClass={stickyClass(3)} stickyStyle={stickyStyle(3)}>Project</Th>}
            {!writersView && <Th w={widthOf("brand")} colKey="brand" onResize={resizeCol} stickyClass={stickyClass(4)} stickyStyle={stickyStyle(4)}>Brand</Th>}
            <Th w={widthOf("pillar")} colKey="pillar" onResize={resizeCol}>Content Pillar</Th>
            {/* Feb 2026 · Onscreen Content — the headline/overlay copy
                that appears IN the creative. Rich-text, auto-saves
                exactly like Caption; surfaced here so writers don't
                have to pop the SmartPostEditor to draft it. */}
            <Th w={widthOf("onscreen")} colKey="onscreen" onResize={resizeCol}>Onscreen content</Th>
            {/* Writer's view blows the Caption column full-width so
                SmartRichText can live inline (no popover). */}
            <Th w={widthOf("caption")} colKey="caption" onResize={resizeCol}>Caption</Th>
            {writersView && <Th w={widthOf("hashtags")} colKey="hashtags" onResize={resizeCol}>Hashtags</Th>}
            <Th w={widthOf("notes")} colKey="notes" onResize={resizeCol}>Internal notes</Th>
            {!writersView && <Th w={widthOf("channels")} colKey="channels" onResize={resizeCol}>Channels</Th>}
            <Th w={widthOf("status")} colKey="status" onResize={resizeCol}>Status</Th>
            <Th w={widthOf("attach")} colKey="attach" onResize={resizeCol}>Attachments / Links</Th>
          </tr>
        </thead>
        <tbody>
          {(groups || [{ key: "", label: "", items: rows }]).map((g) => (
            <React.Fragment key={g.key || "__all__"}>
              {groupBy !== "none" && g.label && (
                <tr className="bg-slate-100/70 sticky" data-testid={`cs-group-${g.key}`}>
                  <td colSpan={99} className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold text-slate-700 border-b border-border">
                    {g.label} <span className="text-slate-500 font-normal normal-case ml-1">— {g.items.length} row{g.items.length === 1 ? "" : "s"}</span>
                  </td>
                </tr>
              )}
              {g.items.map((r) => (
                <SheetRow key={r.id} row={r} canEdit={canEdit} onChange={onRowChange}
                           brands={brands} projects={projects} writersView={writersView}
                           feedback={feedbackByItem[r.id] || []}
                           onMarkRead={onMarkRead}
                           onReply={onReply}
                           wsId={wsId}
                           onFeedbackAdded={onFeedbackAdded}
                           onOpenEditor={onOpenEditor}
                           stickyClass={stickyClass}
                           stickyStyle={stickyStyle}
                           effFrozen={effFrozen}
                           wrapMode={wrapMode} />
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}


/* ──────── Mobile card renderer (writer-first) ───────────────────── */
function SheetCard({ row, canEdit, onChange, brands, projects, writersView = false, feedback = [], onMarkRead, onReply, wsId, onFeedbackAdded, onOpenEditor }) {
  const brandName = brands.find((b) => b.id === row.brand_id)?.name || row.brand_name;
  const projectName = projects.find((p) => p.id === row.project_id)?.name || row.project_name;
  const unreadCount = feedback.filter((f) => !f.read_at).length;
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  return (
    <div className="border border-border bg-background p-3 space-y-2" data-testid={`cs-card-${row.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
          {row.planned_date || "—"}
          {brandName && <span className="ml-2 text-foreground">{brandName}</span>}
          {projectName && <span className="ml-1 text-muted-foreground"> · {projectName}</span>}
        </div>
        {feedback.length > 0 && (
          <RowFeedbackPopover
            open={feedbackOpen}
            onOpenChange={setFeedbackOpen}
            feedback={feedback}
            canEdit={canEdit}
            wsId={wsId}
            rowId={row.id}
            onMarkRead={onMarkRead}
            onReply={onReply}
            onFeedbackAdded={onFeedbackAdded}
            trigger={
              <button type="button"
                      className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 border ${
                        unreadCount > 0 ? "bg-rose-600 text-white border-rose-700" : "bg-muted text-muted-foreground border-border"
                      }`}
                      data-testid={`cs-card-feedback-${row.id}`}>
                <MessageSquare className="w-3 h-3" /> {unreadCount > 0 ? unreadCount : feedback.length}
              </button>
            }
          />
        )}
      </div>
      <div className={`font-semibold text-sm break-words flex items-start gap-1 ${row.status === "published" ? "bg-emerald-50/60 -mx-3 -my-2 px-3 py-2" : ""}`} data-testid={`cs-card-name-${row.id}`}>
        <span className="flex-1 min-w-0">
          {row.name || <span className="text-muted-foreground italic font-normal">Untitled deliverable</span>}
        </span>
        <button type="button"
                 onClick={() => onOpenEditor?.(row.id)}
                 className="shrink-0 text-primary/70 hover:text-primary"
                 title="Open full deliverable editor"
                 data-testid={`cs-card-open-editor-${row.id}`}>
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">Topic</div>
        <AutoSaveCell row={row} field="topic" multiline canEdit={canEdit} onSaved={onChange} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">Onscreen content</div>
        <RichTextCell row={row} field="onscreen_content" canEdit={canEdit}
                       onSaved={onChange} inline={false}
                       labelFull="Onscreen content"
                       placeholderShort="Click to draft onscreen copy…"
                       placeholderLong="Draft the headline / overlay copy that appears IN the creative."
                       showPreview />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">Caption</div>
        <CaptionCell row={row} canEdit={canEdit} open={false} setOpen={() => {}} onSaved={onChange} showPreview />
      </div>
      {writersView && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">Hashtags</div>
          <AutoSaveCell row={row} field="hashtags" multiline canEdit={canEdit} onSaved={onChange} />
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">Internal notes</div>
        <AutoSaveCell row={row} field="internal_notes" multiline canEdit={canEdit} onSaved={onChange} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">Status</div>
          <StatusCell row={row} canEdit={canEdit} onSaved={onChange} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">Content Pillar</div>
          <AutoSaveCell row={row} field="content_pillar" canEdit={canEdit} onSaved={onChange} />
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-0.5">Attachments / Links</div>
        <AttachmentsCell row={row} canEdit={canEdit} open={false} setOpen={() => {}} onSaved={onChange} showPreview />
      </div>
    </div>
  );
}

function Th({ children, w, stickyClass = "", stickyStyle, colKey, onResize }) {
  // Nov 2026 · When the parent flags this cell as sticky, we switch
  // from the default muted header background to a solid `bg-background`
  // (via `stickyClass`) + the cumulative-left offset coming from
  // SheetTable. The header still stays sticky-top via its wrapping
  // <thead> class, so a frozen cell is sticky on BOTH axes.
  // Feb 2026 · Right-edge resize handle. Pointer-based drag updates
  // the width via the onResize callback; parent persists to
  // localStorage so a writer's custom layout survives reloads.
  const thRef = useRef(null);
  const startDrag = (e) => {
    if (!onResize || !colKey) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = thRef.current?.getBoundingClientRect().width || w;
    const onMove = (ev) => {
      const next = startW + (ev.clientX - startX);
      onResize(colKey, next);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  return (
    <th ref={thRef}
         className={`relative text-left px-2 py-2 border-b border-r border-border text-[10px] uppercase tracking-widest text-muted-foreground font-semibold whitespace-nowrap ${stickyClass}`}
         style={{ width: `${w}px`, minWidth: `${w}px`, ...(stickyStyle || {}) }}>
      {children}
      {onResize && colKey && (
        <span
          onPointerDown={startDrag}
          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none group hover:bg-primary/30 active:bg-primary/50"
          data-testid={`cs-col-resize-${colKey}`}
          title="Drag to resize"
        >
          <span className="absolute inset-y-0 right-0 w-px bg-border group-hover:bg-primary" />
        </span>
      )}
    </th>
  );
}

/* ──────── Interakt WhatsApp Business hand-off ──────────────────── */

function ShareInteraktWA({ wsId, shareId }) {
  const [open, setOpen] = useState(false);
  const [toNumber, setToNumber] = useState("");
  const [template, setTemplate] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!toNumber.trim()) { toast.error("Enter a WhatsApp number"); return; }
    if (!template.trim()) { toast.error("Enter the Interakt template name"); return; }
    setSending(true);
    try {
      await api.post(`/workspaces/${wsId}/content-sheet/shares/${shareId}/whatsapp`, {
        to_number: toNumber.trim(),
        template_name: template.trim(),
      });
      toast.success("Sent via Interakt");
      setOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not send WhatsApp"));
    } finally { setSending(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost"
                className="h-7 text-[10px] rounded-none text-emerald-700 hover:bg-emerald-50"
                title="Send review link via WhatsApp Business (Interakt)"
                data-testid={`cs-share-interakt-btn-${shareId}`}>
          <svg viewBox="0 0 24 24" className="w-3 h-3 mr-1" fill="currentColor" aria-hidden="true">
            <path d="M20.52 3.48A12 12 0 0 0 2.5 18.2L2 22l3.9-1.03A12 12 0 1 0 20.52 3.48Z"/>
          </svg>
          WA Business
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-2 rounded-none space-y-1" align="end" data-testid={`cs-share-interakt-panel-${shareId}`}>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
          Interakt WhatsApp
        </div>
        <Input value={toNumber} onChange={(e) => setToNumber(e.target.value)}
               placeholder="+91 9876543210"
               className="h-7 rounded-none text-xs"
               data-testid={`cs-share-interakt-number-${shareId}`} />
        <Input value={template} onChange={(e) => setTemplate(e.target.value)}
               placeholder="Approved template name"
               className="h-7 rounded-none text-xs"
               data-testid={`cs-share-interakt-template-${shareId}`} />
        <details className="text-[10px] text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground">Template variables available</summary>
          <ul className="mt-1 pl-3 list-disc space-y-0.5">
            <li><span className="font-mono">{"{{1}}"}</span> · Client / reviewer name</li>
            <li><span className="font-mono">{"{{2}}"}</span> · Plan label</li>
            <li><span className="font-mono">{"{{3}}"}</span> · Review URL</li>
            <li><span className="font-mono">{"{{4}}"}</span> · Project name</li>
            <li><span className="font-mono">{"{{5}}"}</span> · Brand name</li>
            <li><span className="font-mono">{"{{6}}"}</span> · Expires on</li>
            <li><span className="font-mono">{"{{7}}"}</span> · Sender name</li>
          </ul>
          <p className="mt-1">Uses the workspace Interakt key already configured under Workspace → Integrations.</p>
        </details>
        <Button size="sm" onClick={send} disabled={sending}
                className="h-7 w-full text-[10px] rounded-none"
                data-testid={`cs-share-interakt-send-${shareId}`}>
          {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
          Send via Interakt
        </Button>
      </PopoverContent>
    </Popover>
  );
}


/* ──────── Share analytics popover (opens / viewer identities) ───── */

function ShareOpensPopover({ wsId, shareId, defaultEmail = "", shareLabel = "" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ total: 0, identity_confirmed_count: 0, events: [] });
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reminderEmail, setReminderEmail] = useState(defaultEmail || "");
  const [reminderNote, setReminderNote] = useState("");
  const [sending, setSending] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [opensRes, remRes] = await Promise.all([
        api.get(`/workspaces/${wsId}/content-sheet/shares/${shareId}/opens`),
        api.get(`/workspaces/${wsId}/content-sheet/shares/${shareId}/reminders`),
      ]);
      setData(opensRes.data || { total: 0, events: [] });
      setReminders(Array.isArray(remRes.data) ? remRes.data : []);
      if (!reminderEmail) {
        const lastVerified = (opensRes.data?.events || []).find((e) => e.stage === "identity" && e.reviewer_email);
        if (lastVerified?.reviewer_email) setReminderEmail(lastVerified.reviewer_email);
      }
    } catch (e) { /* soft fail */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);
  const fmtTs = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return (iso || "").slice(0, 16).replace("T", " "); }
  };
  const sendReminder = async () => {
    if (!reminderEmail.trim()) { toast.error("Enter recipient email"); return; }
    setSending(true);
    try {
      await api.post(`/workspaces/${wsId}/content-sheet/shares/${shareId}/remind`, {
        email: reminderEmail.trim(),
        note: reminderNote.trim() || null,
      });
      toast.success("Reminder sent");
      setReminderNote("");
      // Optimistically prepend to the history so the PM doesn't have
      // to re-open the popover to see their own send.
      setReminders((prev) => [{
        id: `local-${Date.now()}`,
        channel: "email",
        to_email: reminderEmail.trim(),
        note: reminderNote.trim() || null,
        sent_by_name: "You",
        created_at: new Date().toISOString(),
      }, ...prev]);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not send reminder")); }
    finally { setSending(false); }
  };
  // Compute idle hours since last verified open (if any).
  const idleHours = (() => {
    const last = data.events.find((e) => e.stage === "identity");
    if (!last?.created_at) return null;
    try { return Math.round((Date.now() - new Date(last.created_at).getTime()) / 3600000); }
    catch { return null; }
  })();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost"
                className="h-7 text-[10px] rounded-none"
                title="Who opened this share"
                data-testid={`cs-share-opens-btn-${shareId}`}>
          <Eye className="w-3 h-3 mr-1" /> Views
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0 rounded-none" align="end" data-testid={`cs-share-opens-panel-${shareId}`}>
        <div className="p-2 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-semibold">
          Share analytics {loading && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}
        </div>
        <div className="p-2 text-xs grid grid-cols-2 gap-2 border-b border-border">
          <div><span className="text-muted-foreground">Total opens</span><div className="font-bold text-lg">{data.total}</div></div>
          <div><span className="text-muted-foreground">Confirmed viewers</span><div className="font-bold text-lg">{data.identity_confirmed_count}</div></div>
        </div>
        {idleHours !== null && idleHours >= 48 && (
          <div className="px-2 py-1 bg-amber-50 border-b border-amber-300 text-[10px] text-amber-900"
               data-testid={`cs-share-opens-stale-${shareId}`}>
            ⚠ No activity for {idleHours}h — consider a nudge.
          </div>
        )}
        <div className="max-h-[220px] overflow-y-auto">
          {data.events.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground italic">
              {loading ? "Loading…" : "No opens yet."}
            </div>
          ) : data.events.slice(0, 20).map((e) => (
            <div key={e.id} className="p-2 border-b border-border last:border-b-0 text-[11px]" data-testid={`cs-share-open-${e.id}`}>
              <div className="flex items-center gap-2">
                {e.stage === "identity"
                  ? <span className="bg-emerald-50 text-emerald-800 border border-emerald-300 text-[9px] uppercase tracking-widest font-semibold px-1 py-0.5">Verified</span>
                  : <span className="bg-muted text-muted-foreground border border-border text-[9px] uppercase tracking-widest font-semibold px-1 py-0.5">Visit</span>}
                <span className="text-muted-foreground">{fmtTs(e.created_at)}</span>
              </div>
              {e.reviewer_name && (
                <div className="mt-0.5">
                  <span className="font-semibold">{e.reviewer_name}</span>
                  {e.reviewer_email && <span className="text-muted-foreground"> · {e.reviewer_email}</span>}
                </div>
              )}
              {e.ip && <div className="text-[10px] text-muted-foreground font-mono">{e.ip}</div>}
            </div>
          ))}
        </div>
        {/* Feb 2026 · Reminder history — show every nudge sent on this
            share so a second team member doesn't double-tap the
            client. Covers both email and WhatsApp (Interakt) sends. */}
        {reminders.length > 0 && (
          <div className="border-t border-border" data-testid={`cs-share-reminders-${shareId}`}>
            <div className="px-2 py-1 bg-muted/40 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground flex items-center justify-between">
              <span>Reminder history · {reminders.length}</span>
              <span className="font-mono normal-case tracking-normal text-[10px]">
                last · {fmtTs(reminders[0].created_at)}
              </span>
            </div>
            <ul className="max-h-[140px] overflow-y-auto">
              {reminders.slice(0, 20).map((r) => (
                <li key={r.id} className="px-2 py-1.5 border-b border-border last:border-b-0 text-[11px]"
                    data-testid={`cs-share-reminder-row-${r.id}`}>
                  <div className="flex items-center gap-2">
                    {r.channel === "whatsapp" ? (
                      <span className="bg-emerald-50 text-emerald-800 border border-emerald-300 text-[9px] uppercase tracking-widest font-semibold px-1 py-0.5">WhatsApp</span>
                    ) : (
                      <span className="bg-sky-50 text-sky-800 border border-sky-300 text-[9px] uppercase tracking-widest font-semibold px-1 py-0.5">Email</span>
                    )}
                    <span className="text-muted-foreground">{fmtTs(r.created_at)}</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0 text-[11px]">
                    <span className="text-muted-foreground">to</span>
                    <span className="font-mono break-all">{r.to_email || r.to_number || "—"}</span>
                    {r.template_name && <span className="text-muted-foreground">· <span className="font-mono text-foreground">{r.template_name}</span></span>}
                  </div>
                  {r.note && (
                    <div className="text-[10px] text-muted-foreground italic whitespace-pre-wrap">
                      "{r.note}"
                    </div>
                  )}
                  <div className="text-[10px] text-muted-foreground">by {r.sent_by_name || "someone"}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="p-2 border-t border-border bg-muted/20 space-y-1">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
            Send a reminder
          </div>
          <Input value={reminderEmail} onChange={(e) => setReminderEmail(e.target.value)}
                 placeholder="client@company.com"
                 className="h-7 rounded-none text-xs"
                 data-testid={`cs-share-reminder-email-${shareId}`} />
          <Textarea value={reminderNote} onChange={(e) => setReminderNote(e.target.value)}
                    placeholder="Optional note (e.g. 'Need approval before Monday')"
                    rows={2} className="rounded-none text-xs"
                    data-testid={`cs-share-reminder-note-${shareId}`} />
          <Button size="sm" onClick={sendReminder}
                  disabled={sending || !reminderEmail.trim()}
                  className="h-7 w-full text-[10px] rounded-none"
                  data-testid={`cs-share-reminder-send-${shareId}`}>
            {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Email reminder
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


/* ──────── Row feedback popover (reread + reply + add comment) ───── */

function RowFeedbackPopover({ open, onOpenChange, trigger, feedback, canEdit, wsId, rowId, onMarkRead, onReply, onFeedbackAdded }) {
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyFor, setReplyFor] = useState(null);  // feedback.id currently being replied to
  const [replyText, setReplyText] = useState("");
  const fmtTs = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return (iso || "").slice(0, 16).replace("T", " "); }
  };
  const postComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/content-sheet/items/${rowId}/comment`,
        { comment: newComment.trim() },
      );
      onFeedbackAdded?.(rowId, data);
      setNewComment("");
      toast.success("Comment posted");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not post comment")); }
    finally { setPosting(false); }
  };
  const sendReply = async (fid) => {
    if (!replyText.trim()) return;
    await onReply?.(fid, replyText);
    setReplyText("");
    setReplyFor(null);
  };
  const sorted = useMemo(
    () => [...feedback].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")),
    [feedback],
  );
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[380px] p-0 rounded-none" align="start" data-testid={`cs-row-feedback-panel-${rowId}`}>
        <div className="p-2 border-b border-border bg-muted/40 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest font-semibold">
            Review thread · {feedback.length}
          </div>
          <div className="flex items-center gap-2">
            {feedback.length > 0 && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/workspaces/${wsId}/content-sheet/items/${rowId}/feedback.pdf`}
                 target="_blank" rel="noreferrer"
                 className="text-[10px] text-primary hover:underline"
                 title="Export this thread as a client-ready PDF"
                 data-testid={`cs-row-feedback-export-${rowId}`}>
                Export PDF
              </a>
            )}
            {feedback.some((f) => !f.read_at) && (
              <button type="button"
                      onClick={async () => {
                        const unread = feedback.filter((f) => !f.read_at);
                        await Promise.all(unread.map((f) => onMarkRead?.(f.id)));
                      }}
                      className="text-[10px] text-primary hover:underline"
                      data-testid={`cs-row-feedback-markread-${rowId}`}>
                Mark all read
              </button>
            )}
          </div>
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {sorted.length === 0 && (
            <div className="p-4 text-center text-xs text-muted-foreground italic">
              No feedback yet — add the first review comment below.
            </div>
          )}
          {sorted.map((f) => {
            const isTeam = f.author_type === "team";
            const toneBg = f.action === "approve"
              ? "border-l-emerald-500"
              : f.action === "reject"
              ? "border-l-rose-500"
              : isTeam
              ? "border-l-primary"
              : "border-l-amber-500";
            return (
              <div key={f.id} className={`p-2 border-b border-border last:border-b-0 border-l-4 ${toneBg}`}
                   data-testid={`cs-row-feedback-item-${f.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs">
                      <span className="font-semibold">{f.reviewer_name || "Client"}</span>
                      <span className="text-muted-foreground"> · {isTeam ? "team" : f.action}</span>
                      {!f.read_at && <span className="ml-1 bg-rose-600 text-white text-[9px] px-1 py-0.5">NEW</span>}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{fmtTs(f.created_at)}</div>
                    {f.comment && (
                      <div className="text-[11px] mt-1 whitespace-pre-wrap break-words">{f.comment}</div>
                    )}
                    {/* Replies */}
                    {Array.isArray(f.replies) && f.replies.length > 0 && (
                      <ul className="mt-1 border-l-2 border-primary/30 pl-2 space-y-1">
                        {f.replies.map((rp) => (
                          <li key={rp.id} className="text-[11px]">
                            <div>
                              <span className="font-semibold text-primary">{rp.author_name} (team)</span>
                              <span className="text-muted-foreground"> · {fmtTs(rp.created_at)}</span>
                            </div>
                            <div className="text-muted-foreground whitespace-pre-wrap break-words">{rp.text}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Reply affordance */}
                    {canEdit && (
                      replyFor === f.id ? (
                        <div className="mt-1 flex gap-1">
                          <Input value={replyText}
                                 onChange={(e) => setReplyText(e.target.value)}
                                 onKeyDown={(e) => { if (e.key === "Enter") sendReply(f.id); }}
                                 placeholder="Reply to the client…"
                                 className="h-7 rounded-none text-xs"
                                 autoFocus
                                 data-testid={`cs-row-reply-input-${f.id}`} />
                          <Button size="sm" onClick={() => sendReply(f.id)} disabled={!replyText.trim()}
                                  className="h-7 text-[10px] rounded-none"
                                  data-testid={`cs-row-reply-send-${f.id}`}>Send</Button>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-1">
                          <button type="button"
                                  onClick={() => { setReplyFor(f.id); setReplyText(""); }}
                                  className="text-[10px] text-primary hover:underline"
                                  data-testid={`cs-row-reply-btn-${f.id}`}>
                            Reply
                          </button>
                          {!f.read_at && (
                            <button type="button" onClick={() => onMarkRead?.(f.id)}
                                    className="text-[10px] text-muted-foreground hover:text-primary"
                                    data-testid={`cs-row-feedback-markread-${f.id}`}>
                              Mark read
                            </button>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {canEdit && (
          <div className="p-2 border-t border-border bg-muted/20">
            <div className="text-[10px] uppercase tracking-widest font-semibold mb-1 text-muted-foreground">
              Add team review comment
            </div>
            <Textarea value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Post a review comment visible to the client in-thread."
                      rows={2}
                      className="rounded-none text-xs"
                      data-testid={`cs-row-add-comment-input-${rowId}`} />
            <div className="flex justify-end mt-1">
              <Button size="sm" onClick={postComment} disabled={!newComment.trim() || posting}
                      className="h-7 text-[10px] rounded-none"
                      data-testid={`cs-row-add-comment-send-${rowId}`}>
                {posting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />}
                Post comment
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


/* ──────── Single row with per-cell auto-save ─────────────────────── */

function SheetRow({ row, canEdit, onChange, brands, projects, writersView = false, feedback = [], onMarkRead, onReply, wsId, onFeedbackAdded, onOpenEditor, stickyClass = () => "", stickyStyle = () => undefined, effFrozen = 0, wrapMode = "nowrap" }) {
  const brandName = useMemo(() => brands.find((b) => b.id === row.brand_id)?.name || row.brand_name, [brands, row.brand_id, row.brand_name]);
  const projectName = useMemo(() => projects.find((p) => p.id === row.project_id)?.name || row.project_name, [projects, row.project_id, row.project_name]);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Nov 2026 · Td now takes an optional `colIndex` so a frozen column
  // can read the cumulative sticky offset + solid bg handed down from
  // SheetTable. When unfrozen, falls back to the plain border styling.
  // The row-level `wrapMode` picks the cell overflow strategy:
  //   wrap   → whitespace-normal, no height clamp
  //   nowrap → whitespace-normal (default row behaviour)
  //   crop   → whitespace-nowrap + overflow-hidden + truncate + max-h
  const wrapTdCls = wrapMode === "crop"
    ? "truncate whitespace-nowrap overflow-hidden [&_*]:truncate [&_*]:whitespace-nowrap [&_*]:overflow-hidden"
    : wrapMode === "wrap"
      ? "whitespace-normal align-top"
      : "";
  const Td = ({ children, className = "", colIndex }) => {
    const sticky = colIndex != null && colIndex < effFrozen;
    const sc = sticky ? "sticky z-10 bg-background shadow-[inset_-1px_0_0_0_hsl(var(--border))]" : "";
    const ss = sticky ? { left: stickyOffsetsLocal[colIndex] } : undefined;
    return (
      <td className={`align-top border-b border-r border-border p-1 ${sc} ${wrapTdCls} ${className}`}
            style={ss}>
        {children}
      </td>
    );
  };
  // Mirror SheetTable's offset array so Td can resolve `left` by index
  // without re-plumbing it as a prop.
  const stickyOffsetsLocal = (writersView
    ? [110, 200, 200, 140, 320, 520, 200, 200, 120, 240]
    : [110, 200, 200, 110, 120, 140, 260, 320, 200, 160, 120, 200])
    .reduce((acc, w, i, src) => { acc.push(i === 0 ? 0 : (acc[i - 1] + src[i - 1])); return acc; }, []);
  const unreadCount = useMemo(() => feedback.filter((f) => !f.read_at).length, [feedback]);
  const hasAnyFeedback = feedback.length > 0;
  const hasUnread = unreadCount > 0;
  // Nov 2026 · Published rows get a very faint emerald tint ONLY on
  // the Deliverable column so PMs can scan the sheet for "what's live"
  // at a glance without the rest of the row changing colour.
  const isPublished = (row.status || "").toLowerCase() === "published";
  return (
    <tr className={`hover:bg-muted/30 ${hasUnread ? "bg-amber-50/40" : ""} ${wrapMode === "crop" ? "max-h-8" : ""}`} data-testid={`cs-row-${row.id}`}>
      <Td className="text-[11px] font-mono text-muted-foreground px-2 py-2" colIndex={0}>
        <div className="flex items-center gap-1 flex-wrap">
          {/* Feb 2026 · The review-comments icon is now ALWAYS visible
              when any feedback exists (read or unread) so the writer /
              PM can re-open the thread, respond, or post a new
              review comment at any time. */}
          <RowFeedbackPopover
            open={feedbackOpen}
            onOpenChange={setFeedbackOpen}
            feedback={feedback}
            canEdit={canEdit}
            wsId={wsId}
            rowId={row.id}
            onMarkRead={onMarkRead}
            onReply={onReply}
            onFeedbackAdded={onFeedbackAdded}
            trigger={
              hasAnyFeedback ? (
                <button type="button"
                        className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 border ${hasUnread
                          ? "bg-rose-600 text-white border-rose-700"
                          : "bg-muted text-muted-foreground border-border hover:bg-background hover:text-foreground"}`}
                        title={hasUnread
                          ? `${unreadCount} unread · ${feedback.length} total`
                          : `${feedback.length} review feedback · click to reread or respond`}
                        data-testid={`cs-row-feedback-${row.id}`}>
                  <MessageSquare className="w-2.5 h-2.5" />
                  {hasUnread ? unreadCount : feedback.length}
                </button>
              ) : canEdit ? (
                /* No feedback yet — PM-only "add review comment" shortcut */
                <button type="button"
                        className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-muted-foreground/60 hover:text-primary border border-dashed border-border px-1 py-0.5"
                        title="Add a review comment"
                        data-testid={`cs-row-add-feedback-${row.id}`}>
                  <MessageSquare className="w-2.5 h-2.5" />
                </button>
              ) : null
            }
          />
          {row.planned_date || "—"}
        </div>
      </Td>
      <Td colIndex={1} className={isPublished ? "bg-emerald-50/70" : ""}>
        {/* Feb 2026 · Deliverable name stays read-only (PM-owned via
            Content Calendar) but now carries a hyperlink that opens
            the exact same SmartPostEditor dialog from the Content
            Calendar page — one tap to edit anything beyond caption
            / status without leaving your mental model.
            Nov 2026 · Switched the "open in new window" <a> to a
            button that triggers the parent-mounted modal editor so
            the PM stays on the sheet (keeping filter + scroll state).
            Published rows get a faint emerald tint on this column only
            for at-a-glance "what's live" scanning. */}
        <div className="px-1 py-1.5 text-xs whitespace-pre-wrap flex items-start gap-1" data-testid={`cs-readonly-name-${row.id}`}>
          <span className="flex-1 min-w-0" title={row.name || ""}>
            {row.name || <span className="text-muted-foreground italic">Untitled deliverable</span>}
          </span>
          <button type="button"
                   onClick={() => onOpenEditor?.(row.id)}
                   className="shrink-0 text-primary/70 hover:text-primary"
                   title="Open full deliverable editor"
                   data-testid={`cs-row-open-editor-${row.id}`}>
            <Pencil className="w-3 h-3" />
          </button>
        </div>
        {/* Latest-feedback inline summary. */}
        {feedback.length > 0 && (() => {
          const latest = [...feedback].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
          const relTs = (iso) => {
            if (!iso) return "";
            const t = new Date(iso).getTime();
            const diff = (Date.now() - t) / 1000;
            if (diff < 60) return "just now";
            if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
            if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
            if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
            return new Date(iso).toLocaleDateString("en", { month: "short", day: "numeric" });
          };
          const tone = latest.action === "approve"
            ? "text-emerald-700"
            : latest.action === "reject"
            ? "text-rose-700"
            : "text-muted-foreground";
          const verb = latest.action === "approve" ? "approved"
            : latest.action === "reject" ? "rejected" : "commented";
          return (
            <div className={`mt-0.5 text-[10px] ${tone} flex items-center gap-1`}
                 title={`${latest.reviewer_name} ${verb} on ${new Date(latest.created_at || "").toLocaleString()}`}
                 data-testid={`cs-row-latest-feedback-${row.id}`}>
              <span className="font-semibold truncate max-w-[90px]">{latest.reviewer_name}</span>
              <span>· {verb}</span>
              <span className="text-muted-foreground">· {relTs(latest.created_at)}</span>
            </div>
          );
        })()}
      </Td>
      <Td colIndex={2}>
        {/* Feb 2026 · Topic is now writable from the Sheet so writers
            don't have to leave for the Content Calendar to tune the
            weekly hook — same auto-save contract as every other
            editable cell. */}
        <AutoSaveCell row={row} field="topic" multiline canEdit={canEdit} onSaved={onChange} />
      </Td>
      {!writersView && (
        <Td className="text-[11px] px-2 py-2 truncate" title={projectName} colIndex={3}>{projectName || "—"}</Td>
      )}
      {!writersView && (
        <Td className="text-[11px] px-2 py-2 truncate" title={brandName} colIndex={4}>{brandName || "—"}</Td>
      )}
      <Td>
        <AutoSaveCell row={row} field="content_pillar" canEdit={canEdit} onSaved={onChange} />
      </Td>
      <Td>
        {/* Feb 2026 · Onscreen content — SmartRichText popover, same
            auto-save contract as Caption. Writer view opens it inline. */}
        <RichTextCell row={row} field="onscreen_content" canEdit={canEdit}
                       onSaved={onChange} inline={writersView}
                       labelFull="Onscreen content"
                       placeholderShort="Click to draft onscreen copy…"
                       placeholderLong="Draft the headline / overlay copy that appears IN the creative." />
      </Td>
      <Td>
        <CaptionCell row={row} canEdit={canEdit}
                      open={captionOpen} setOpen={setCaptionOpen}
                      onSaved={onChange}
                      inline={writersView} />
      </Td>
      {writersView && (
        <Td>
          {/* Hashtags stored as array on content_items; we round-trip
              as a space-separated string for inline editing. */}
          <HashtagsCell row={row} canEdit={canEdit} onSaved={onChange} />
        </Td>
      )}
      <Td>
        <AutoSaveCell row={row} field="internal_notes" multiline canEdit={canEdit} onSaved={onChange} />
      </Td>
      {!writersView && (
        <Td>
          <ChannelsCell row={row} canEdit={canEdit} onSaved={onChange} />
        </Td>
      )}
      <Td>
        <StatusCell row={row} canEdit={canEdit} onSaved={onChange} />
      </Td>
      <Td>
        <AttachmentsCell row={row} canEdit={canEdit} open={attachOpen} setOpen={setAttachOpen} onSaved={onChange} showPreview={writersView} />
      </Td>
    </tr>
  );
}

/* ──────── Auto-save text cell (debounced 5s + flush on blur) ───── */

const AUTOSAVE_MS = 5000;

function AutoSaveCell({ row, field, canEdit, multiline = false, onSaved }) {
  const [val, setVal] = useState(row[field] || "");
  const [state, setState] = useState("idle"); // idle | dirty | saving | saved | error
  const timerRef = useRef(null);
  // Keep local state in sync when row is externally refreshed.
  useEffect(() => { setVal(row[field] || ""); }, [row[field]]);
  const flush = async () => {
    if (state !== "dirty") return;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setState("saving");
    try {
      await api.patch(`/content-items/${row.id}`, { [field]: val });
      setState("saved");
      onSaved?.(row.id, { [field]: val });
      setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1200);
    } catch (e) {
      setState("error");
      toast.error(apiErrorMessage(e, "Save failed"));
    }
  };
  const onChange = (next) => {
    setVal(next);
    setState("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, AUTOSAVE_MS);
  };
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  const cls = "w-full text-xs bg-transparent border-0 rounded-none focus:outline-none focus:bg-background focus:border focus:border-primary/40 px-1 py-0.5";
  return (
    <div className="relative group" data-testid={`cs-cell-${field}-${row.id}`}>
      {multiline ? (
        <Textarea
          value={val}
          onChange={(e) => onChange(e.target.value)}
          onBlur={flush}
          disabled={!canEdit}
          rows={2}
          className={cls + " resize-none min-h-[2.4em]"}
          data-testid={`cs-input-${field}-${row.id}`}
        />
      ) : (
        <Input
          type="text"
          value={val}
          onChange={(e) => onChange(e.target.value)}
          onBlur={flush}
          disabled={!canEdit}
          className={"h-7 " + cls}
          data-testid={`cs-input-${field}-${row.id}`}
        />
      )}
      <SaveState state={state} />
    </div>
  );
}

function SaveState({ state }) {
  if (state === "saving") return <Loader2 className="w-3 h-3 absolute top-1 right-1 animate-spin text-muted-foreground" />;
  if (state === "saved") return <CheckCircle2 className="w-3 h-3 absolute top-1 right-1 text-emerald-600" />;
  if (state === "dirty") return <span className="w-1.5 h-1.5 absolute top-2 right-1 rounded-full bg-amber-500" title="Unsaved — will save in 5s or on blur" />;
  if (state === "error") return <X className="w-3 h-3 absolute top-1 right-1 text-rose-600" />;
  return null;
}

/* Caption — SmartRichText in a popover (or inline in Writer's view) */

function CaptionCell({ row, canEdit, open, setOpen, onSaved, inline = false }) {
  const [val, setVal] = useState(row.caption || "");
  const [state, setState] = useState("idle");
  const timerRef = useRef(null);
  useEffect(() => { setVal(row.caption || ""); }, [row.caption]);
  const flush = async () => {
    if (state !== "dirty") return;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setState("saving");
    try {
      await api.patch(`/content-items/${row.id}`, { caption: val });
      setState("saved");
      onSaved?.(row.id, { caption: val });
      setTimeout(() => setState((s) => s === "saved" ? "idle" : s), 1200);
    } catch (e) { setState("error"); toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const onEditChange = (next) => {
    setVal(next);
    setState("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, AUTOSAVE_MS);
  };
  // Feb 2026 · Writer's view opens the SmartRichText inline (no
  // popover) so writers can type freely in the full column width.
  if (inline) {
    return (
      <div className="px-1 py-1" onBlur={flush} data-testid={`cs-caption-inline-${row.id}`}>
        <SmartRichText
          value={val}
          onChange={onEditChange}
          placeholder="Draft the caption. /slash for formatting, @mentions, media."
          mediaMode="image"
          workspaceId={row.workspace_id}
          disabled={!canEdit}
          allowAiImprove
          testid={`cs-caption-rich-${row.id}`}
        />
        <div className="flex items-center justify-end mt-1 text-[10px] text-muted-foreground">
          <SaveState state={state} />
          {state === "dirty" && <span className="ml-1">· auto-save in 5s</span>}
        </div>
      </div>
    );
  }
  const stripHtml = (h) => (h || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const preview = stripHtml(val);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) flush(); }}>
      <PopoverTrigger asChild>
        <button type="button"
                 className="w-full text-left px-1 py-1.5 min-h-[2.4em] text-xs hover:bg-background border border-transparent hover:border-primary/40 cursor-text"
                 data-testid={`cs-caption-btn-${row.id}`}>
          <span className="line-clamp-2">{preview || <span className="text-muted-foreground italic">Click to write caption…</span>}</span>
          <SaveState state={state} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[520px] p-2 rounded-none" align="start">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Caption</div>
        <SmartRichText
          value={val}
          onChange={onEditChange}
          placeholder="Draft the caption. Use /slash for formatting, @mentions, media."
          mediaMode="image"
          workspaceId={row.workspace_id}
          disabled={!canEdit}
          allowAiImprove
          testid={`cs-caption-rich-${row.id}`}
        />
        <div className="flex items-center justify-between mt-2 text-[10px]">
          <span className="text-muted-foreground">{state === "saving" ? "Saving…" : state === "saved" ? "Saved" : state === "dirty" ? "Will auto-save in 5s" : ""}</span>
          <button type="button" onClick={() => { flush(); setOpen(false); }}
                   className="text-primary hover:underline"
                   data-testid={`cs-caption-close-${row.id}`}>Done</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* Hashtags — inline list editor. Stored as array, round-trips as
   space-separated string so writers can type freely. */

/* RichTextCell — generic SmartRichText popover (parameterised field).
   Same auto-save contract as CaptionCell but accepts any HTML field
   on the row. Used for Onscreen Content; keeps CaptionCell's
   test-id surface intact to avoid regressing existing e2e tests. */

function RichTextCell({ row, field, canEdit, onSaved, inline = false,
                        labelFull = "Rich text",
                        placeholderShort = "Click to write…",
                        placeholderLong = "Draft the content.",
                        showPreview = false }) {
  const [val, setVal] = useState(row[field] || "");
  const [state, setState] = useState("idle");
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => { setVal(row[field] || ""); }, [row[field]]);
  const flush = async () => {
    if (state !== "dirty") return;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setState("saving");
    try {
      await api.patch(`/content-items/${row.id}`, { [field]: val });
      setState("saved");
      onSaved?.(row.id, { [field]: val });
      setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1200);
    } catch (e) { setState("error"); toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const onEditChange = (next) => {
    setVal(next);
    setState("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, AUTOSAVE_MS);
  };
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  if (inline) {
    return (
      <div className="px-1 py-1" onBlur={flush} data-testid={`cs-${field}-inline-${row.id}`}>
        <SmartRichText
          value={val}
          onChange={onEditChange}
          placeholder={placeholderLong}
          mediaMode="image"
          workspaceId={row.workspace_id}
          disabled={!canEdit}
          allowAiImprove
          testid={`cs-${field}-rich-${row.id}`}
        />
        <div className="flex items-center justify-end mt-1 text-[10px] text-muted-foreground">
          <SaveState state={state} />
          {state === "dirty" && <span className="ml-1">· auto-save in 5s</span>}
        </div>
      </div>
    );
  }
  const stripHtml = (h) => (h || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const preview = stripHtml(val);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) flush(); }}>
      <PopoverTrigger asChild>
        <button type="button"
                 className="w-full text-left px-1 py-1.5 min-h-[2.4em] text-xs hover:bg-background border border-transparent hover:border-primary/40 cursor-text"
                 data-testid={`cs-${field}-btn-${row.id}`}>
          <span className={showPreview ? "" : "line-clamp-2"}>
            {preview || <span className="text-muted-foreground italic">{placeholderShort}</span>}
          </span>
          <SaveState state={state} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[520px] p-2 rounded-none" align="start">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">{labelFull}</div>
        <SmartRichText
          value={val}
          onChange={onEditChange}
          placeholder={placeholderLong}
          mediaMode="image"
          workspaceId={row.workspace_id}
          disabled={!canEdit}
          allowAiImprove
          testid={`cs-${field}-rich-${row.id}`}
        />
        <div className="flex items-center justify-between mt-2 text-[10px]">
          <span className="text-muted-foreground">{state === "saving" ? "Saving…" : state === "saved" ? "Saved" : state === "dirty" ? "Will auto-save in 5s" : ""}</span>
          <button type="button" onClick={() => { flush(); setOpen(false); }}
                   className="text-primary hover:underline"
                   data-testid={`cs-${field}-close-${row.id}`}>Done</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function HashtagsCell({ row, canEdit, onSaved }) {
  const toStr = (arr) => (Array.isArray(arr) ? arr.join(" ") : (arr || ""));
  const [val, setVal] = useState(toStr(row.hashtags));
  const [state, setState] = useState("idle");
  const timerRef = useRef(null);
  useEffect(() => { setVal(toStr(row.hashtags)); /* eslint-disable-next-line */ }, [JSON.stringify(row.hashtags)]);
  const flush = async () => {
    if (state !== "dirty") return;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setState("saving");
    try {
      const tags = (val.match(/#?[\w\u00C0-\uFFFF][\w\u00C0-\uFFFF-]*/g) || [])
                   .map((t) => (t.startsWith("#") ? t : `#${t}`));
      await api.patch(`/content-items/${row.id}`, { hashtags: tags });
      setState("saved");
      onSaved?.(row.id, { hashtags: tags });
      setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1200);
    } catch (e) { setState("error"); toast.error(apiErrorMessage(e, "Save failed")); }
  };
  return (
    <div className="relative" onBlur={flush}>
      <Textarea
        value={val}
        onChange={(e) => {
          setVal(e.target.value);
          setState("dirty");
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(flush, AUTOSAVE_MS);
        }}
        placeholder="#brand #campaign #cta"
        rows={2}
        disabled={!canEdit}
        className="rounded-none text-[11px] min-h-[2.4em] bg-transparent border-transparent hover:border-input focus:border-primary"
        data-testid={`cs-hashtags-${row.id}`}
      />
      <SaveState state={state} />
    </div>
  );
}

/* Channels — multi-select chips */

function ChannelsCell({ row, canEdit, onSaved }) {
  const [channels, setChannels] = useState(row.channels || []);
  const [state, setState] = useState("idle");
  useEffect(() => { setChannels(row.channels || []); }, [row.channels]);
  const toggle = async (val) => {
    if (!canEdit) return;
    const next = channels.includes(val) ? channels.filter((x) => x !== val) : [...channels, val];
    setChannels(next);
    setState("saving");
    try {
      await api.patch(`/content-items/${row.id}`, { channels: next });
      setState("saved");
      onSaved?.(row.id, { channels: next });
      setTimeout(() => setState("idle"), 1000);
    } catch (e) { setState("error"); toast.error(apiErrorMessage(e, "Save failed")); }
  };
  return (
    <div className="relative flex flex-wrap gap-0.5 px-1 py-1" data-testid={`cs-channels-${row.id}`}>
      {CHANNELS.map((c) => {
        const on = channels.includes(c.value);
        return (
          <button key={c.value} type="button"
                   disabled={!canEdit}
                   onClick={() => toggle(c.value)}
                   className={`text-[9px] font-semibold border px-1 py-0.5 uppercase ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary"}`}
                   title={c.value}
                   data-testid={`cs-channel-${row.id}-${c.value}`}>
            {c.label}
          </button>
        );
      })}
      <SaveState state={state} />
    </div>
  );
}

/* Status — inline select */

function StatusCell({ row, canEdit, onSaved }) {
  const [val, setVal] = useState(row.status || "planned");
  const [state, setState] = useState("idle");
  useEffect(() => { setVal(row.status || "planned"); }, [row.status]);
  const save = async (next) => {
    setVal(next);
    setState("saving");
    try {
      await api.patch(`/content-items/${row.id}`, { status: next });
      setState("saved");
      onSaved?.(row.id, { status: next });
      setTimeout(() => setState("idle"), 1000);
    } catch (e) { setState("error"); toast.error(apiErrorMessage(e, "Save failed")); }
  };
  return (
    <div className="relative">
      <Select value={val} onValueChange={save} disabled={!canEdit}>
        <SelectTrigger className="h-7 text-xs rounded-none" data-testid={`cs-status-${row.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
        </SelectContent>
      </Select>
      <SaveState state={state} />
    </div>
  );
}

/* Attachments + external links */

function AttachmentsCell({ row, canEdit, open, setOpen, onSaved, showPreview = false }) {
  const atts = row.attachments || [];
  const links = row.external_links || [];
  const imgs = atts.filter((a) => a.is_image);
  const other = atts.filter((a) => !a.is_image);
  // Feb 2026 · Paste-from-clipboard — writers often copy a thumbnail
  // or product photo and want to attach it without first saving to
  // disk. We watch for ClipboardEvent.items of type image/* and treat
  // them like a normal file upload. The handler lives at the trigger
  // button + popover so Chrome/Firefox/Safari all fire the event.
  const handleClipPaste = async (e) => {
    if (!canEdit) return;
    const items = e.clipboardData?.items;
    if (!items || !items.length) return;
    const imageItems = [...items].filter((it) => it.kind === "file" && it.type?.startsWith("image/"));
    if (imageItems.length === 0) return;
    e.preventDefault();
    for (const it of imageItems) {
      const blob = it.getAsFile();
      if (!blob) continue;
      try {
        const fd = new FormData();
        const ext = (blob.type.split("/")[1] || "png").split("+")[0];
        const named = new File([blob], blob.name || `pasted-${Date.now()}.${ext}`, { type: blob.type });
        fd.append("file", named);
        const { data } = await api.post(`/content-items/${row.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const next = [...(row.attachments || []), data];
        onSaved?.(row.id, { attachments: next });
        toast.success("Pasted image attached");
      } catch (err) { toast.error(apiErrorMessage(err, "Paste upload failed")); }
    }
  };
  // Feb 2026 · Writer's view bumps thumbnail size + shows the first
  // image's filename alongside the chip so writers scanning the
  // column don't have to open the popover to tell deliverables apart.
  const thumbSize = showPreview ? "w-12 h-12" : "w-7 h-7";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
                 onPaste={handleClipPaste}
                 className={`w-full flex flex-wrap gap-1 items-center px-1 py-1 ${showPreview ? "min-h-[3.2em]" : "min-h-[2.4em]"} hover:bg-background border border-transparent hover:border-primary/40`}
                 data-testid={`cs-att-btn-${row.id}`}
                 title={canEdit ? "Click to manage · paste (Ctrl+V) to attach an image" : ""}>
          {imgs.slice(0, showPreview ? 3 : 4).map((a) => (
            <img key={a.id} src={a.url} alt={a.name}
                 className={`${thumbSize} object-cover border border-border`}
                 title={a.name} />
          ))}
          {other.length > 0 && (
            <span className="inline-flex items-center text-[10px] bg-muted border border-border px-1 py-0.5">
              <Paperclip className="w-2.5 h-2.5 mr-0.5" /> {other.length}
            </span>
          )}
          {links.length > 0 && (
            <span className="inline-flex items-center text-[10px] bg-muted border border-border px-1 py-0.5">
              <Link2 className="w-2.5 h-2.5 mr-0.5" /> {links.length}
            </span>
          )}
          {atts.length === 0 && links.length === 0 && (
            <span className="text-muted-foreground italic text-[10px]">
              {canEdit ? "Add files / links · paste image" : "—"}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-3 rounded-none" align="end" data-testid={`cs-att-popover-${row.id}`}
                       onPaste={handleClipPaste}>
        <AttachmentsManager row={row} canEdit={canEdit} onSaved={onSaved} />
      </PopoverContent>
    </Popover>
  );
}

function AttachmentsManager({ row, canEdit, onSaved }) {
  const [atts, setAtts] = useState(row.attachments || []);
  const [links, setLinks] = useState(row.external_links || []);
  const [uploading, setUploading] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  useEffect(() => { setAtts(row.attachments || []); setLinks(row.external_links || []); }, [row]);
  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/content-items/${row.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = [...atts, data];
      setAtts(next);
      onSaved?.(row.id, { attachments: next });
      toast.success("Attached");
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setUploading(false); }
  };
  const removeAtt = async (id) => {
    try {
      await api.delete(`/content-items/${row.id}/attachments/${id}`);
      const next = atts.filter((a) => a.id !== id);
      setAtts(next);
      onSaved?.(row.id, { attachments: next });
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };
  const addLink = async () => {
    if (!linkUrl.trim()) return;
    try {
      const { data } = await api.post(`/content-items/${row.id}/external-links`, { url: linkUrl, label: linkLabel || linkUrl });
      const next = [...links, data];
      setLinks(next);
      onSaved?.(row.id, { external_links: next });
      setLinkUrl(""); setLinkLabel("");
    } catch (e) { toast.error(apiErrorMessage(e, "Add link failed")); }
  };
  const removeLink = async (id) => {
    try {
      await api.delete(`/content-items/${row.id}/external-links/${id}`);
      const next = links.filter((l) => l.id !== id);
      setLinks(next);
      onSaved?.(row.id, { external_links: next });
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };
  return (
    <div className="space-y-3 text-xs">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Files</div>
        {atts.length === 0 && <div className="text-muted-foreground italic">No files yet</div>}
        <div className="flex flex-wrap gap-1 mb-2">
          {atts.map((a) => (
            <div key={a.id} className="relative group border border-border p-0.5 bg-muted">
              {a.is_image
                ? <img src={a.url} alt={a.name} className="w-14 h-14 object-cover" />
                : <div className="w-14 h-14 flex items-center justify-center text-[10px] text-muted-foreground p-1 text-center">{a.name?.slice(0, 20)}</div>}
              <a href={a.url} target="_blank" rel="noreferrer"
                 className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition">
                <ExternalLink className="w-3 h-3 text-white" />
              </a>
              {canEdit && (
                <button type="button" onClick={() => removeAtt(a.id)}
                         className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                         title="Remove"
                         data-testid={`cs-att-remove-${a.id}`}>×</button>
              )}
            </div>
          ))}
        </div>
        {canEdit && (
          <label className="inline-flex items-center gap-1 cursor-pointer border border-primary/40 bg-primary/5 text-primary px-2 py-1 text-[11px] hover:bg-primary hover:text-primary-foreground">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Add file
            <input type="file" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])}
                   data-testid={`cs-att-upload-${row.id}`} />
          </label>
        )}
      </div>
      <div className="border-t border-border pt-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">External links</div>
        {links.length === 0 && <div className="text-muted-foreground italic mb-1">No links yet</div>}
        <ul className="space-y-0.5 mb-2">
          {links.map((l) => (
            <li key={l.id} className="flex items-center gap-1 text-[11px]">
              <Link2 className="w-3 h-3 text-muted-foreground shrink-0" />
              <a href={l.url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate flex-1" title={l.url}>
                {l.label || l.url}
              </a>
              {canEdit && (
                <button type="button" onClick={() => removeLink(l.id)}
                         className="text-muted-foreground hover:text-rose-600 shrink-0"
                         title="Remove"
                         data-testid={`cs-link-remove-${l.id}`}>×</button>
              )}
            </li>
          ))}
        </ul>
        {canEdit && (
          <div className="flex gap-1">
            <Input type="url" placeholder="https://… (video / drive / figma)"
                    value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
                    className="h-7 text-[11px] rounded-none"
                    data-testid={`cs-link-url-${row.id}`} />
            <Button size="sm" onClick={addLink} className="h-7 text-[10px] rounded-none"
                    data-testid={`cs-link-add-${row.id}`}>Add</Button>
          </div>
        )}
      </div>
    </div>
  );
}


/* ──────── Inbox item (bell dropdown row) ───────────────────────── */

function InboxItem({ fb, onMarkRead }) {
  const actionIcon = fb.action === "approve"
    ? <CheckIcon className="w-3 h-3 text-emerald-700" />
    : fb.action === "reject"
    ? <X className="w-3 h-3 text-rose-700" />
    : <MessageSquare className="w-3 h-3 text-amber-700" />;
  const actionLabel = fb.action === "approve" ? "approved"
    : fb.action === "reject" ? "rejected" : "commented on";
  return (
    <div className="p-2 border-b border-border last:border-b-0 hover:bg-muted/40" data-testid={`cs-inbox-item-${fb.id}`}>
      <div className="flex items-start gap-2">
        <div className="mt-0.5">{actionIcon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-xs">
            <span className="font-semibold">{fb.reviewer_name || "Client"}</span>
            <span className="text-muted-foreground"> {actionLabel} an item</span>
          </div>
          {fb.comment && (
            <div className="text-[11px] text-muted-foreground whitespace-pre-wrap line-clamp-3 mt-0.5">
              {fb.comment}
            </div>
          )}
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {fb.reviewer_email && <span>{fb.reviewer_email} · </span>}
            {fb.created_at?.slice(0, 16).replace("T", " ")}
          </div>
        </div>
        <button type="button" onClick={onMarkRead}
                className="text-[10px] text-primary hover:underline shrink-0"
                data-testid={`cs-inbox-markread-${fb.id}`}>
          Mark read
        </button>
      </div>
    </div>
  );
}


/* ──────── Share dialog · Phase 2 ────────────────────────────────── */

function ShareDialog({ wsId, currentFilters, onClose }) {
  const [shares, setShares] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    label: "",
    client_name: "",
    expires_at: "",       // date input (YYYY-MM-DD)
    project_id: (currentFilters?.project_ids?.[0]) || currentFilters?.project_id || "",
    allow_approve: true,
    allow_reject: true,
    allow_comment: true,
  });
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/content-sheet/shares`);
      setShares(data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load shares"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);
  useEffect(() => {
    if (!wsId) return;
    projectsLookup(wsId).then((rows) => setProjects(rows || []));
  }, [wsId]);

  const makeShare = async () => {
    if (!draft.project_id) {
      toast.error("Pick a project — shares are project-scoped");
      return;
    }
    setCreating(true);
    try {
      // Normalise expires_at — accept YYYY-MM-DD, treat as end-of-day
      // in local tz, store as ISO.
      let expires_at = null;
      if (draft.expires_at) {
        const d = new Date(`${draft.expires_at}T23:59:59`);
        expires_at = d.toISOString();
      }
      const payload = {
        project_id: draft.project_id,
        filters: {
          month: currentFilters?.month || null,
          date_from: currentFilters?.date_from || null,
          date_to: currentFilters?.date_to || null,
          brand_id: currentFilters?.brand_id || (currentFilters?.brand_ids?.length === 1 ? currentFilters.brand_ids[0] : null),
          project_id: draft.project_id,
          client_id: currentFilters?.client_id || (currentFilters?.client_ids?.length === 1 ? currentFilters.client_ids[0] : null),
          status: currentFilters?.status || null,
          category: currentFilters?.category || null,
          q: currentFilters?.q || null,
        },
        expires_at,
        allow_approve: draft.allow_approve,
        allow_reject: draft.allow_reject,
        allow_comment: draft.allow_comment,
        label: draft.label || null,
        client_name: draft.client_name || null,
      };
      const { data } = await api.post(`/workspaces/${wsId}/content-sheet/shares`, payload);
      toast.success("Share link created");
      setDraft({ label: "", client_name: "", expires_at: "",
                  project_id: draft.project_id,
                  allow_approve: true, allow_reject: true, allow_comment: true });
      await load();
      // Auto-copy the new link so the first thing the user can do is paste it.
      const url = `${window.location.origin}/share/content-sheet/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.info("Link copied to clipboard"); } catch {}
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not create share"));
    } finally { setCreating(false); }
  };

  const revoke = async (sid) => {
    if (!window.confirm("Revoke this share link? Anyone with the URL will see a 'revoked' message.")) return;
    try {
      await api.delete(`/workspaces/${wsId}/content-sheet/shares/${sid}`);
      await load();
      toast.success("Share revoked");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Revoke failed"));
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/share/content-sheet/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { toast.error("Clipboard not available — select and copy manually"); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="cs-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-4 h-4" /> Share Content Sheet
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Public tokened link — the client sees the rows that match the current filters, with approve / reject / comment controls per row.
            No login needed. Revoke any time.
          </p>
        </DialogHeader>

        <div className="space-y-3">
          <div className="border border-border p-3 bg-muted/30">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">New share</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Project <span className="text-rose-600">*</span></label>
                <Select value={draft.project_id || "_pick"}
                        onValueChange={(v) => setDraft({ ...draft, project_id: v === "_pick" ? "" : v })}>
                  <SelectTrigger className="h-8 rounded-none text-xs" data-testid="cs-share-project">
                    <SelectValue placeholder="— pick a project —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_pick">— pick a project —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Clients only see this project's deliverables and the brand attached to it.
                </p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Label (optional)</label>
                <Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                        placeholder="e.g. October 2026 plan for Acme"
                        className="h-8 rounded-none text-xs" data-testid="cs-share-label" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Client name (optional)</label>
                <Input value={draft.client_name} onChange={(e) => setDraft({ ...draft, client_name: e.target.value })}
                        placeholder="Pre-fills Reviewed-by on each decision"
                        className="h-8 rounded-none text-xs" data-testid="cs-share-client-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Expires on (optional)</label>
                <Input type="date" value={draft.expires_at} onChange={(e) => setDraft({ ...draft, expires_at: e.target.value })}
                        className="h-8 rounded-none text-xs" data-testid="cs-share-expires" />
              </div>
              <div className="flex items-end gap-3 flex-wrap text-xs">
                <label className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={draft.allow_approve}
                          onChange={(e) => setDraft({ ...draft, allow_approve: e.target.checked })}
                          data-testid="cs-share-allow-approve" />
                  Approve
                </label>
                <label className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={draft.allow_reject}
                          onChange={(e) => setDraft({ ...draft, allow_reject: e.target.checked })}
                          data-testid="cs-share-allow-reject" />
                  Reject
                </label>
                <label className="inline-flex items-center gap-1">
                  <input type="checkbox" checked={draft.allow_comment}
                          onChange={(e) => setDraft({ ...draft, allow_comment: e.target.checked })}
                          data-testid="cs-share-allow-comment" />
                  Comment
                </label>
              </div>
            </div>
            <div className="flex justify-end mt-2">
              <Button size="sm" onClick={makeShare} disabled={creating}
                       className="h-8 rounded-none text-xs" data-testid="cs-share-create">
                {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                Create share link
              </Button>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              Existing shares {loading && <Loader2 className="w-3 h-3 inline animate-spin" />}
            </div>
            <div className="max-h-[280px] overflow-y-auto border border-border">
              {shares.length === 0 ? (
                <div className="p-4 text-center text-xs text-muted-foreground italic">
                  No shares yet — the first one you create will appear here.
                </div>
              ) : shares.map((s) => {
                const url = `${window.location.origin}/share/content-sheet/${s.token}`;
                const expired = s.expires_at && s.expires_at < new Date().toISOString();
                const revoked = Boolean(s.revoked_at);
                return (
                  <div key={s.id} className={`p-2 border-b border-border last:border-b-0 ${revoked ? "opacity-50" : ""}`}
                        data-testid={`cs-share-row-${s.id}`}>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                        <div className="font-semibold truncate">{s.label || "Untitled share"}</div>
                        <div className="text-[10px] text-muted-foreground truncate break-all" title={url}>{url}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-auto">
                      {revoked && <span className="text-[9px] uppercase tracking-widest font-bold text-rose-700 bg-rose-50 border border-rose-300 px-1 py-0.5">Revoked</span>}
                      {!revoked && expired && <span className="text-[9px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-300 px-1 py-0.5">Expired</span>}
                      {!revoked && !expired && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => copyLink(s.token)}
                                   className="h-7 text-[10px] rounded-none"
                                   data-testid={`cs-share-copy-${s.id}`}>
                            <Copy className="w-3 h-3 mr-1" /> Copy
                          </Button>
                          <ShareOpensPopover wsId={wsId} shareId={s.id} />
                          {/* Feb 2026 · One-tap WhatsApp share. Opens
                              wa.me with the review URL + a short
                              greeting pre-filled so the PM doesn't
                              have to paste the link in chat. */}
                          <a href={(() => {
                              const greet = s.client_name ? `Hi ${s.client_name}, ` : "Hi, ";
                              const msg = `${greet}here's the content plan for your review. You can approve, reject or comment on each item right in your browser: ${url}`;
                              return `https://wa.me/?text=${encodeURIComponent(msg)}`;
                            })()}
                             target="_blank" rel="noreferrer"
                             className="inline-flex items-center h-7 text-[10px] rounded-none px-2 border border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                             title="Send review link via WhatsApp"
                             data-testid={`cs-share-whatsapp-${s.id}`}>
                            <svg viewBox="0 0 24 24" className="w-3 h-3 mr-1" fill="currentColor" aria-hidden="true">
                              <path d="M20.52 3.48A12 12 0 0 0 2.5 18.2L2 22l3.9-1.03A12 12 0 1 0 20.52 3.48Zm-8.5 18a9.98 9.98 0 0 1-5.1-1.4l-.36-.22-2.9.76.78-2.82-.24-.37A9.97 9.97 0 1 1 12.02 21.5Zm5.47-7.47c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.47-.89-.79-1.49-1.76-1.66-2.06-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.6-.92-2.2-.24-.57-.49-.49-.67-.5l-.57-.01c-.2 0-.52.07-.8.37s-1.05 1.03-1.05 2.5c0 1.47 1.08 2.9 1.23 3.1.15.2 2.12 3.23 5.14 4.53 2.4 1.03 2.88.82 3.39.78.51-.05 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.08-.12-.27-.2-.57-.35Z"/>
                            </svg>
                            WhatsApp
                          </a>
                          <ShareInteraktWA wsId={wsId} shareId={s.id} />
                          <Button size="sm" variant="ghost" onClick={() => revoke(s.id)}
                                   className="h-7 text-[10px] text-rose-700 hover:text-rose-900 rounded-none"
                                   data-testid={`cs-share-revoke-${s.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Revoke
                          </Button>
                        </>
                      )}
                      </div>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                      <span>Created {s.created_at?.slice(0, 10)} by {s.created_by_name || "—"}</span>
                      {s.expires_at && <span>Expires {s.expires_at.slice(0, 10)}</span>}
                      {s.client_name && <span>· For {s.client_name}</span>}
                      <span className="ml-auto">
                        {s.feedback_total || 0} feedback
                        {s.feedback_unread > 0 && (
                          <span className="ml-1 inline-block text-[9px] text-background bg-rose-600 px-1 py-0.5" title="Unread">
                            {s.feedback_unread} new
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="cs-share-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

